import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Cable, Briefcase, MapPin, Phone, Mail } from "lucide-react";
import { COMPANY } from "../../lib/api";

const REPAIR_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8556163b-a21a-4889-85bf-57bee7a466d6/images/b24fe5e4a06fbd0bf91767ba9c911ea4f16fd81d762d2fc9e67928c11065581f.png";
const CONSTRUCTION_IMG =
  "https://static.prod-images.emergentagent.com/jobs/8556163b-a21a-4889-85bf-57bee7a466d6/images/6bfa0d4d4856d94bc939edc8fcea720222115c33fe5497df645e1ff5266684af.png";

// Services teaser
export function ServicesTeaser() {
  return (
    <section className="relative py-24 sm:py-28 bg-[#050505]" data-testid="home-services-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-6 items-end mb-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="overline mb-4">01 / Υπηρεσίες</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Τι κάνουμε.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed">
              Από τη βλάβη στο πεδίο, έως το πλήρες έργο υποδομής οπτικών ινών.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#27272A]">
          <Teaser
            tag="01"
            title="Αποκατάσταση Βλαβών"
            desc="Άμεση επέμβαση και αποκατάσταση βλαβών σε δίκτυα οπτικών ινών με fusion splicing και OTDR."
            image={REPAIR_IMG}
            icon={Wrench}
          />
          <Teaser
            tag="02"
            title="Κατασκευές Εντός & Εκτός"
            desc="Έργα υποδομής οπτικών ινών σε εξωτερικούς χώρους και εντός των εγκαταστάσεων του πελάτη."
            image={CONSTRUCTION_IMG}
            icon={Cable}
          />
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            to="/services"
            data-testid="home-services-cta"
            className="btn-outline px-7 py-3 text-sm font-medium inline-flex items-center gap-2"
          >
            Όλες οι Υπηρεσίες <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Teaser({ tag, title, desc, image, icon: Icon }) {
  return (
    <article className="group bg-[#0F0F11] overflow-hidden">
      <div className="aspect-[16/10] overflow-hidden border-b border-[#27272A]">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-8">
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono-th text-xs tracking-[0.25em] text-[#E63946]">
            {tag} / SERVICE
          </span>
          <Icon size={22} strokeWidth={1.5} className="text-white/80" />
        </div>
        <h3 className="font-heading text-xl text-white mb-3 font-medium tracking-tight">
          {title}
        </h3>
        <p className="text-[#A1A1AA] text-sm leading-relaxed">{desc}</p>
      </div>
    </article>
  );
}

// About teaser
export function AboutTeaser() {
  return (
    <section className="relative py-24 sm:py-28 bg-[#0F0F11] border-y border-[#27272A]" data-testid="home-about-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <div className="overline mb-4">02 / Εταιρεία</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight">
              Γιατί να μας
              <br />
              <span className="text-[#E63946]">επιλέξετε.</span>
            </h2>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <Metric value="15+" label="Έτη εμπειρίας" />
              <Metric value="500+" label="Έργα" />
              <Metric value="100%" label="Πιστοποιημένα" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <p className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed">
              Με έδρα τους Αγίους Αναργύρους, η{" "}
              <span className="text-white">Technological Horizons</span>{" "}
              εγγυάται την απόλυτη αξιοπιστία στις τηλεπικοινωνιακές σας
              υποδομές. Η συνεργασία μας με τους μεγαλύτερους παρόχους
              αποτελεί την πιο ηχηρή απόδειξη της ποιότητας των έργων μας.
            </p>
            <div className="mt-8">
              <Link
                to="/about"
                data-testid="home-about-cta"
                className="btn-outline px-7 py-3 text-sm font-medium inline-flex items-center gap-2"
              >
                Περισσότερα για εμάς <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Metric({ value, label }) {
  return (
    <div className="border-l border-[#27272A] pl-3">
      <div className="font-heading text-2xl sm:text-3xl text-white font-medium tracking-tighter">
        {value}
      </div>
      <div className="mt-1 text-xs text-[#A1A1AA] leading-snug">{label}</div>
    </div>
  );
}

// Careers teaser
export function CareersTeaser() {
  return (
    <section className="relative py-24 sm:py-28 bg-[#050505]" data-testid="home-careers-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-[#27272A] p-10 sm:p-14 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline mb-4">03 / Καριέρα</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white leading-tight">
              Ελάτε στην
              <span className="text-[#E63946]"> ομάδα</span> μας.
            </h2>
            <p className="mt-4 text-[#A1A1AA] leading-relaxed max-w-2xl">
              Ψάχνουμε πάντα τεχνικούς που μοιράζονται το πάθος μας για άρτια
              κατασκευή και ταχύτητα στην αποκατάσταση. Δείτε τις ανοιχτές
              θέσεις εργασίας.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
            <Link
              to="/careers"
              data-testid="home-careers-cta"
              className="btn-primary px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2"
            >
              <Briefcase size={16} /> Δείτε Θέσεις
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Contact teaser
export function ContactTeaser() {
  return (
    <section className="relative py-24 sm:py-28 bg-[#0F0F11] border-t border-[#27272A]" data-testid="home-contact-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-6">
            <div className="overline mb-4">04 / Επικοινωνία</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight">
              Ας μιλήσουμε για
              <br />
              το <span className="text-[#E63946]">έργο σας.</span>
            </h2>
            <p className="mt-5 text-[#A1A1AA] leading-relaxed max-w-lg">
              Είμαστε διαθέσιμοι για διαγνώσεις, προσφορές και αναφορές βλάβης.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                data-testid="home-contact-cta"
                className="btn-primary px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2"
              >
                Στείλτε Μήνυμα <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <div className="border border-[#27272A] divide-y divide-[#27272A]">
              <ContactRow icon={MapPin} label="Έδρα" value={COMPANY.address} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(COMPANY.address)}`} />
              <ContactRow icon={Phone} label="Τηλέφωνο" value={COMPANY.phone} href={`tel:${COMPANY.phone}`} />
              <ContactRow icon={Phone} label="Κινητό" value={COMPANY.mobile} href={`tel:${COMPANY.mobile}`} />
              <ContactRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      className="flex items-center gap-5 px-5 py-4 bg-[#0F0F11] hover:bg-[#16161A] transition-colors group"
    >
      <div className="w-10 h-10 border border-[#27272A] flex items-center justify-center group-hover:border-[#E63946] transition-colors">
        <Icon size={16} className="text-[#E63946]" />
      </div>
      <div>
        <div className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
          {label}
        </div>
        <div className="text-white text-sm sm:text-base font-medium">
          {value}
        </div>
      </div>
    </a>
  );
}
